import React, { useCallback, useEffect, useState } from 'react';
import { sitesService, siteColour } from '../../_services/sites.service';
import { errorText } from './addLocation/locationCopy';
import './multiLocation.css';

/**
 * "Bank for this location", for an owner with more than one location (or a
 * location whose own account is part-way set up). Says which bank this
 * location pays into and who shares it, and lets the owner give it its own:
 * that opens a new Stripe account for the location and sends them to Stripe
 * to add the bank. Until Stripe has the bank, the location keeps paying into
 * the account it's on (the server moves it across when Stripe is ready, the
 * next time Payouts is read — i.e. on the way back from Stripe).
 *
 * Used on Settings → Payouts and in setup (Getting paid). Renders nothing
 * for single-location owners, anyone who isn't the owner, or while the
 * feature is switched off.
 */
const bankText = (bank) => (bank ? `${bank.bankName || 'Bank'} ••••${bank.last4}` : 'No bank yet');
const names = (sites) => sites.map((s) => s.salonName).filter(Boolean);
const listed = (arr) => (arr.length <= 1 ? arr.join('') : `${arr.slice(0, -1).join(', ')} and ${arr[arr.length - 1]}`);

export default function LocationPayoutCard({ returnPath, variant = 'settings', onChanged }) {
    const [sum, setSum] = useState(null);
    const [busy, setBusy] = useState(null);
    const [error, setError] = useState('');
    const [picking, setPicking] = useState(false);

    const load = useCallback(() => sitesService.payoutAccounts()
        .then((r) => { setSum(r || null); return r; })
        .catch(() => { setSum(null); return null; }), []);
    useEffect(() => { load(); }, [load]);

    if (!sum || !sum.enabled) return null;
    const salonId = localStorage.getItem('salonId');
    const current = sum.accounts.find((a) => a.id === sum.currentId) || null;
    const totalSites = sum.accounts.reduce((n, a) => n + a.sites.length, 0);
    if (totalSites < 2 && !sum.pending) return null;

    const here = current ? current.sites.find((s) => String(s.salonId) === String(salonId)) : null;
    const hereName = (here && here.salonName) || 'This location';
    const sharedWith = current ? names(current.sites.filter((s) => String(s.salonId) !== String(salonId))) : [];
    const others = [
        ...sum.accounts.filter((a) => a.id !== sum.currentId && a.bank),
        ...(sum.unused || []).filter((a) => a.bank).map((a) => ({ ...a, sites: [] })),
    ];
    const back = returnPath || `${window.location.pathname}${window.location.search}`;

    const toStripe = async () => {
        const { url } = await sitesService.payoutOnboardingLink(back);
        if (!url) throw new Error('Stripe couldn’t open the set-up page. Try again.');
        window.location.assign(url);
    };
    const run = async (key, fn) => {
        if (busy) return;
        setBusy(key);
        setError('');
        try {
            await fn();
        } catch (e) {
            setError(errorText(e));
            setBusy(null);
        }
    };
    const separate = () => run('separate', async () => {
        await sitesService.separatePayoutAccount();
        await toStripe(); // leaves the page
    });
    const finish = () => run('finish', toStripe);
    const use = (id) => run(`use:${id}`, async () => {
        await sitesService.usePayoutAccount(id);
        await load();
        setPicking(false);
        setBusy(null);
        if (onChanged) onChanged();
    });
    const recheck = () => run('check', async () => {
        const r = await load();
        setBusy(null);
        if (r && !r.pending && onChanged) onChanged();
    });

    const Wrap = ({ children }) => (variant === 'settings'
        ? (
            <div className="settings-card ml-pay">
                <div className="settings-card-header"><h2>Bank for this location</h2><p>Each location can pay into the same bank or its own. Changes apply to new payments; money already paid stays where it went.</p></div>
                {children}
            </div>
        )
        : <div className="ml-pay ml-pay--inline">{children}</div>);

    if (sum.pending) {
        const p = sum.pending;
        return (
            <Wrap>
                <div className="ml-pay-row">
                    <span className="ml-dot" style={{ background: siteColour(salonId) }} />
                    <div className="ml-pay-main">
                        <div className="ml-pay-title">{hereName}’s own bank <span className="ml-pay-tag">{p.detailsSubmitted ? 'Stripe is checking' : 'Not finished'}</span></div>
                        <div className="ml-pay-sub">
                            {p.detailsSubmitted
                                ? `Stripe has the details and is checking them. Until it’s done, payments here keep going to ${current ? bankText(current.bank) : 'your current bank'}.`
                                : `Add the bank in Stripe to finish. Until then, payments here keep going to ${current ? bankText(current.bank) : 'your current bank'}, so nothing is held up.`}
                        </div>
                    </div>
                </div>
                <div className="ml-pay-actions">
                    {p.detailsSubmitted
                        ? <button type="button" className="settings-btn settings-btn-secondary" onClick={recheck} disabled={!!busy}>{busy === 'check' ? 'Checking…' : 'Check again'}</button>
                        : <button type="button" className="settings-btn settings-btn-primary" onClick={finish} disabled={!!busy}>{busy === 'finish' ? 'Opening Stripe…' : 'Finish on Stripe'}</button>}
                    {current && <button type="button" className="ml-pay-link" onClick={() => use(current.id)} disabled={!!busy}>Keep using {bankText(current.bank)} instead</button>}
                </div>
                {error && <p className="ml-wizard-error" role="alert">{error}</p>}
            </Wrap>
        );
    }

    return (
        <Wrap>
            <div className="ml-pay-row">
                <span className="ml-dot" style={{ background: siteColour(salonId) }} />
                <div className="ml-pay-main">
                    <div className="ml-pay-title">{current ? bankText(current.bank) : 'Your bank'}</div>
                    <div className="ml-pay-sub">
                        {sharedWith.length
                            ? `Shared with ${listed(sharedWith)}. Payouts arrive together; the payout report splits them by location. Editing this bank changes it for all of them.`
                            : `Only ${hereName} pays into this bank.`}
                    </div>
                </div>
            </div>
            <div className="ml-pay-actions">
                {sharedWith.length > 0 && (
                    <button type="button" className="settings-btn settings-btn-primary" onClick={separate} disabled={!!busy}>{busy === 'separate' ? 'Opening Stripe…' : 'Use a separate bank for this location'}</button>
                )}
                {others.length > 0 && !picking && (
                    <button type="button" className="ml-pay-link" onClick={() => setPicking(true)} disabled={!!busy}>Pay into another of your banks</button>
                )}
            </div>
            {sharedWith.length > 0 && <p className="ml-pay-note">For a different company, or to keep {hereName}’s money apart. Stripe asks for the business and bank details (about five minutes).</p>}
            {picking && (
                <div className="ml-pay-pick" role="group" aria-label="Pay into">
                    {others.map((a) => (
                        <button key={a.id} type="button" className="ml-pay-option" onClick={() => use(a.id)} disabled={!!busy}>
                            <span className="ml-pay-option-bank">{bankText(a.bank)}</span>
                            <span className="ml-pay-option-sub">{a.sites.length ? `Used by ${listed(names(a.sites))}` : 'Not used by a location'}</span>
                            {busy === `use:${a.id}` && <span className="ml-pay-option-sub">Switching…</span>}
                        </button>
                    ))}
                    <button type="button" className="ml-pay-link" onClick={() => setPicking(false)} disabled={!!busy}>Cancel</button>
                </div>
            )}
            {error && <p className="ml-wizard-error" role="alert">{error}</p>}
        </Wrap>
    );
}
